import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, LockResetOutlined as LockIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';

const MIN_LEN = 12;

function passwordChecks(pw) {
  return {
    length: pw.length >= MIN_LEN,
    letter: /[a-zA-Z]/.test(pw),
    digit: /[0-9]/.test(pw),
  };
}

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const resetPassword = useAuthStore((s) => s.resetPassword);

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const checks = useMemo(() => passwordChecks(form.password), [form.password]);
  const strength = useMemo(() => (Object.values(checks).filter(Boolean).length / 3) * 100, [checks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('El enlace de recuperación no es válido.');
      return;
    }
    if (!Object.values(checks).every(Boolean)) {
      setError(`La contraseña debe tener al menos ${MIN_LEN} caracteres, combinando letras y números`);
      return;
    }
    if (form.password !== form.confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    const res = await resetPassword(token, form.password);
    setLoading(false);
    if (res.success) {
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } else {
      setError(res.error || 'No se pudo restablecer la contraseña');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: 4, bgcolor: 'background.paper' }}>
            {done ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <LockIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Contraseña actualizada</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Ya puedes iniciar sesión con tu nueva contraseña. Te llevamos al inicio de sesión…
                </Typography>
                <Button component={Link} to="/login" variant="contained" size="large">Iniciar sesión</Button>
              </Box>
            ) : (
              <>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <LockIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Nueva contraseña</Typography>
                  <Typography variant="body1" color="text.secondary">
                    Elige una contraseña segura para tu cuenta
                  </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Nueva contraseña"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {form.password.length > 0 && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={strength}
                        color={strength === 100 ? 'success' : strength >= 50 ? 'warning' : 'error'}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {checks.length ? '✓' : '○'} {MIN_LEN}+ caracteres &nbsp;
                        {checks.letter ? '✓' : '○'} letras &nbsp;
                        {checks.digit ? '✓' : '○'} números
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    margin="normal"
                    required
                    sx={{ mt: 2 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={loading}
                    sx={{ py: 1.5, mt: 3, fontWeight: 600 }}
                  >
                    {loading ? 'Guardando…' : 'Cambiar contraseña'}
                  </Button>
                </form>
              </>
            )}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ResetPassword;
